import { useState, useContext, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
  Switch,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { AuthContext } from "../../context/AuthContext";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import avatarImage from "../../assets/avatar.png";
import cameraIcon from "../../assets/camera.svg";
import AddFamilyMemberModal from "./AddFamilyMemberModal";
import AddTraderModal from "./AddTraderModal";
import ChangeTraderPasswordModal from "./ChangeTraderPasswordModal";
import BasketManageSection from "./BasketManageSection";
import TableColumnPreferencesSection from "./TableColumnPreferencesSection";
import { getClients, deleteClients, editClients, createClients, generateToken } from "../../api/clients";
import { getTraders, createTrader, updateTrader, deleteTrader, changeTraderPassword } from "../../api/traders";
import { uploadClientTokensFile } from "../../api/users";
import { getClientsWithMargin } from "../../api/orderInitiation";
import {
  getBaskets,
  createBasket,
  addClientsToBasket,
  removeBasketClients,
  deleteBasket,
} from "../../api/baskets";
import { enrichBasketMembers } from "../../utils/basketMembers";
import styles from "./SettingsPage.module.css";

const SETTINGS_TABS_BASE = new Set([
  "personal-info",
  "client-account",
  "groups",
  "table-columns",
]);

function resolveSettingsTabFromSearch(searchParams, isMaster, isTrader) {
  const raw = searchParams.get("tab");
  if (!raw) return "personal-info";
  const v = raw.trim();
  if (v === "client-account" && isTrader) return "personal-info";
  if (SETTINGS_TABS_BASE.has(v)) return v;
  if (v === "traders" && isMaster) return v;
  return "personal-info";
}

function isSettingsTabParamAllowed(tabParam, isMaster, isTrader) {
  if (!tabParam) return true;
  const v = String(tabParam).trim();
  if (v === "traders") return isMaster;
  if (v === "client-account") return !isTrader;
  return SETTINGS_TABS_BASE.has(v);
}

function mapClientToMember(client) {
  return {
    id: client.id,
    fullName: client.full_name || "",
    brokerClientCode: client.broker_client_code || "",
    email: client.email || "",
    status: client.is_active ? "Active" : "Inactive",
    raw: client,
  };
}

/** Compact iOS-style switch; indigo on / soft gray off (matches app chrome) */
const IOS_TRADING_STATUS_SWITCH_SX = {
  width: 38,
  height: 22,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "2px",
    transitionDuration: "220ms",
    transitionTimingFunction: "cubic-bezier(0.36, 0.66, 0.04, 1)",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "var(--text-on-accent)",
      "& + .MuiSwitch-track": {
        background: "linear-gradient(180deg, var(--accent-gradient-start) 0%, var(--accent-gradient-end) 100%)",
        opacity: 1,
        border: "none",
        boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.2)",
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      outline: "2px solid var(--accent-soft-strong)",
      outlineOffset: 1,
    },
    "&.Mui-disabled": {
      opacity: 0.55,
      "&.Mui-checked + .MuiSwitch-track": {
        background: "var(--accent-soft-strong)",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 18,
    height: 18,
    backgroundColor: "var(--bg-primary)",
    boxShadow:
      "0 2px 5px var(--shadow-key), 0 1px 1px var(--shadow-key), 0 0 0 0.5px var(--border-muted)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 11,
    backgroundColor: "var(--bg-tertiary)",
    opacity: 1,
    border: "1px solid var(--border-default)",
    boxShadow: "inset 0 1px 1px var(--border-muted)",
  },
};

function RemoveEntityDialog({ open, onClose, title, children, confirmLabel, loading, error, onConfirm }) {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (loading) return;
        onClose();
      }}
      aria-labelledby="remove-entity-dialog-title"
      aria-busy={loading}
      slotProps={{
        paper: {
          className: `${styles.removeDialogPaper} ${loading ? styles.removeDialogPaperLoading : ""}`,
        },
      }}
    >
      {loading ? (
        <LinearProgress
          color="primary"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            borderRadius: "12px 12px 0 0",
            zIndex: 2,
            "& .MuiLinearProgress-bar": { borderRadius: "12px 12px 0 0" },
          }}
        />
      ) : null}
      <DialogContent className={styles.removeDialogContent}>
        <div className={styles.removeDialogIconWrap} aria-hidden>
          <WarningAmberRoundedIcon sx={{ fontSize: 30 }} />
        </div>
        <h2 id="remove-entity-dialog-title" className={styles.removeDialogHeading}>
          {title}
        </h2>
        <div className={styles.removeDialogBody}>{children}</div>
        {error ? (
          <Alert severity="error" sx={{ mt: 1.5, fontFamily: "Inter, sans-serif", fontSize: "0.75rem" }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions className={styles.removeDialogActions}>
        <Button
          type="button"
          variant="outlined"
          className={styles.removeDialogCancelBtn}
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          className={styles.removeDialogConfirmBtn}
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? "Removing…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SettingsPage() {
  const { auth } = useContext(AuthContext);
  const roleUpper = String(auth.user?.role || "").toUpperCase();
  const isMaster = roleUpper === "MASTER";
  const isTrader = roleUpper === "TRADER";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const raw = searchParams.get("tab");
    if (raw?.trim() === "client-health") {
      navigate("/client-health", { replace: true });
    }
  }, [searchParams, navigate]);

  const activeTab = useMemo(
    () => resolveSettingsTabFromSearch(searchParams, isMaster, isTrader),
    [searchParams, isMaster, isTrader]
  );

  const navigateToSettingsTab = useCallback(
    (tab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === "personal-info") next.delete("tab");
          else next.set("tab", tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    const raw = searchParams.get("tab");
    if (!raw) return;
    if (!isSettingsTabParamAllowed(raw, isMaster, isTrader)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, isMaster, isTrader, setSearchParams]);

  const personalInfoFromAuth = useMemo(
    () => ({
      fullName: auth.user?.full_name || auth.user?.username || "",
      email: auth.user?.email || "",
      phone: auth.user?.phone || auth.user?.phone_number || "",
    }),
    [
      auth.user?.full_name,
      auth.user?.username,
      auth.user?.email,
      auth.user?.phone,
      auth.user?.phone_number,
    ]
  );

  const [tokenMessage, setTokenMessage] = useState(null);

  const handleCloseTokenSnackbar = useCallback((_event, reason) => {
    if (reason === "clickaway") return;
    setTokenMessage(null);
  }, []);

  const isTokenLoading = tokenMessage?.type === "loading";

  const [clientMembers, setClientMembers] = useState([]);
  const [clientMembersLoading, setClientMembersLoading] = useState(true);
  const [clientMembersError, setClientMembersError] = useState(null);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [addClientModalOpen, setAddClientModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [clientDeleteLoading, setClientDeleteLoading] = useState(false);
  const [clientDeleteError, setClientDeleteError] = useState(null);
  const [clientTradingStatusBusyIds, setClientTradingStatusBusyIds] = useState(() => new Set());
  const clientTradingStatusBusyRef = useRef(new Set());
  const tokenUploadInputRef = useRef(null);
  const [tokenUploadLoading, setTokenUploadLoading] = useState(false);
  const [generatingTokenId, setGeneratingTokenId] = useState(null);

  const handleGenerateToken = useCallback(async (id) => {
    if (id == null) return;
    setGeneratingTokenId(id);
    setTokenMessage({ type: "loading", text: "Generating token…" });
    try {
      await generateToken(id);
      setTokenMessage({ type: "success", text: "Token generated successfully" });
    } catch (err) {
      const data = err?.response?.data;
      const text =
        (typeof data?.detail === "string" && data.detail) ||
        (typeof data?.message === "string" && data.message) ||
        err?.message ||
        "Failed to generate token";
      setTokenMessage({ type: "error", text });
    } finally {
      setGeneratingTokenId(null);
    }
  }, []);

  const fetchClientMembers = useCallback(async () => {
    setClientMembersLoading(true);
    setClientMembersError(null);
    try {
      const data = await getClients();
      setClientMembers(Array.isArray(data) ? data.map(mapClientToMember) : []);
    } catch (err) {
      setClientMembersError(err?.response?.data?.detail || err?.message || "Failed to load clients");
      setClientMembers([]);
    } finally {
      setClientMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "client-account") {
      fetchClientMembers();
    } else {
      setClientSearchQuery("");
    }
  }, [activeTab, fetchClientMembers]);

  const filteredClientMembers = useMemo(() => {
    const q = clientSearchQuery.trim().toLowerCase();
    if (!q) return clientMembers;
    return clientMembers.filter((m) => {
      const broker = m.raw?.broker_name || "";
      const haystack = [m.fullName, m.brokerClientCode, m.email, m.status, broker, m.raw?.username]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [clientMembers, clientSearchQuery]);

  const [tradersList, setTradersList] = useState([]);
  const [tradersLoading, setTradersLoading] = useState(false);
  const [tradersError, setTradersError] = useState(null);
  const [traderModalOpen, setTraderModalOpen] = useState(false);
  const [editingTrader, setEditingTrader] = useState(null);
  const [traderPasswordTarget, setTraderPasswordTarget] = useState(null);
  const [traderToDelete, setTraderToDelete] = useState(null);
  const [traderDeleteLoading, setTraderDeleteLoading] = useState(false);
  const [traderDeleteError, setTraderDeleteError] = useState(null);

  const fetchTraders = useCallback(async () => {
    setTradersLoading(true);
    setTradersError(null);
    try {
      const data = await getTraders();
      setTradersList(Array.isArray(data) ? data : []);
    } catch (err) {
      setTradersError(err?.response?.data?.detail || err?.message || "Failed to load traders");
      setTradersList([]);
    } finally {
      setTradersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "traders" && isMaster) {
      fetchTraders();
    }
  }, [activeTab, isMaster, fetchTraders]);

  const [basketName, setBasketName] = useState("");
  const [basketDescription, setBasketDescription] = useState("");
  const [baskets, setBaskets] = useState([]);
  const [basketsLoading, setBasketsLoading] = useState(false);
  const [basketsListError, setBasketsListError] = useState(null);
  const [basketSubmitting, setBasketSubmitting] = useState(false);
  const [basketFormError, setBasketFormError] = useState(null);
  const [selectedBasketId, setSelectedBasketId] = useState(null);
  const [marginClients, setMarginClients] = useState([]);
  const [marginClientsLoading, setMarginClientsLoading] = useState(false);
  const [clientsForBasketError, setClientsForBasketError] = useState(null);
  const [basketClientOpLoading, setBasketClientOpLoading] = useState({
    adding: false,
    removingId: null,
    deleting: false,
  });

  const fetchBaskets = useCallback(async () => {
    setBasketsLoading(true);
    setBasketsListError(null);
    try {
      const list = await getBaskets();
      setBaskets(list);
    } catch (err) {
      console.error("Fetch baskets failed:", err);
      setBasketsListError(err?.response?.data?.detail || err?.message || "Failed to load baskets");
      setBaskets([]);
    } finally {
      setBasketsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "groups") {
      fetchBaskets();
    }
  }, [activeTab, fetchBaskets]);

  const fetchMarginClientsForBaskets = useCallback(async () => {
    setMarginClientsLoading(true);
    setClientsForBasketError(null);
    try {
      const data = await getClientsWithMargin();
      const list = Array.isArray(data) ? data : data?.response ?? [];
      setMarginClients(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Fetch margin clients failed:", err);
      setClientsForBasketError(
        err?.response?.data?.detail || err?.message || "Failed to load clients"
      );
      setMarginClients([]);
    } finally {
      setMarginClientsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "groups") {
      fetchMarginClientsForBaskets();
    }
  }, [activeTab, fetchMarginClientsForBaskets]);

  const selectedBasket = useMemo(
    () => baskets.find((b) => b.id === selectedBasketId) ?? null,
    [baskets, selectedBasketId]
  );

  const selectedBasketMembers = useMemo(
    () => enrichBasketMembers(selectedBasket, marginClients),
    [selectedBasket, marginClients]
  );

  useEffect(() => {
    if (selectedBasketId && !baskets.some((b) => b.id === selectedBasketId)) {
      setSelectedBasketId(null);
    }
  }, [baskets, selectedBasketId]);

  const handleAddClientsToBasket = useCallback(
    async (clientIds) => {
      if (!selectedBasket?.id) return;
      setBasketClientOpLoading((p) => ({ ...p, adding: true }));
      try {
        await addClientsToBasket({ basket_id: selectedBasket.id, client_ids: clientIds });
        await fetchBaskets();
      } finally {
        setBasketClientOpLoading((p) => ({ ...p, adding: false }));
      }
    },
    [selectedBasket, fetchBaskets]
  );

  const handleRemoveBasketMember = useCallback(
    async (clientId) => {
      if (!selectedBasket?.id || clientId == null) return;
      const idNum = Number(clientId);
      if (Number.isNaN(idNum)) return;
      setBasketClientOpLoading((p) => ({ ...p, removingId: String(idNum) }));
      try {
        await removeBasketClients({
          basket_id: selectedBasket.id,
          client_ids: [idNum],
        });
        await fetchBaskets();
      } finally {
        setBasketClientOpLoading((p) => ({ ...p, removingId: null }));
      }
    },
    [selectedBasket, fetchBaskets]
  );

  const handleDeleteSelectedBasket = useCallback(async () => {
    if (!selectedBasket?.id) return;
    setBasketClientOpLoading((p) => ({ ...p, deleting: true }));
    try {
      await deleteBasket(selectedBasket.id);
      setSelectedBasketId(null);
      await fetchBaskets();
    } finally {
      setBasketClientOpLoading((p) => ({ ...p, deleting: false }));
    }
  }, [selectedBasket, fetchBaskets]);

  const handleBasketFormCancel = useCallback(() => {
    setBasketName("");
    setBasketDescription("");
    setBasketFormError(null);
  }, []);

  const handleBasketFormSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setBasketFormError(null);
      const name = basketName.trim();
      if (!name) {
        setBasketFormError("Basket name is required");
        return;
      }
      setBasketSubmitting(true);
      try {
        await createBasket({
          basket_name: name,
          description: basketDescription,
        });
        setBasketName("");
        setBasketDescription("");
        await fetchBaskets();
      } catch (err) {
        console.error("Create basket failed:", err);
        setBasketFormError(
          err?.response?.data?.detail || err?.message || "Failed to create basket"
        );
      } finally {
        setBasketSubmitting(false);
      }
    },
    [basketName, basketDescription, fetchBaskets]
  );

  const handleAddClientMember = useCallback(
    async (payload) => {
      try {
        await createClients(payload);
        await fetchClientMembers();
        setAddClientModalOpen(false);
      } catch (err) {
        console.error("Create client failed:", err);
        throw err;
      }
    },
    [fetchClientMembers]
  );

  const handleUploadTokensClick = useCallback(() => {
    tokenUploadInputRef.current?.click();
  }, []);

  const handleTokenFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      setTokenUploadLoading(true);
      setTokenMessage({ type: "loading", text: "Uploading token file…" });
      try {
        await uploadClientTokensFile(file);
        setTokenMessage(null);
        navigate("/client-health");
      } catch (err) {
        console.error("Upload client token file failed:", err);
        const data = err?.response?.data;
        const text =
          (typeof data?.detail === "string" && data.detail) ||
          (typeof data?.message === "string" && data.message) ||
          err?.message ||
          "Failed to upload client token file";
        setTokenMessage({ type: "error", text });
      } finally {
        setTokenUploadLoading(false);
      }
    },
    [navigate]
  );

  const handleEditClientMember = useCallback(
    async (id, payload) => {
      try {
        await editClients(id, payload);
        await fetchClientMembers();
        setEditingClient(null);
        setAddClientModalOpen(false);
      } catch (err) {
        console.error("Edit client failed:", err);
        throw err;
      }
    },
    [fetchClientMembers]
  );

  const handleClientTradingStatusChange = useCallback(
    async (member, nextActive) => {
      const raw = member?.raw;
      const id = raw?.id;
      if (id == null) return;
      if (clientTradingStatusBusyRef.current.has(id)) return;
      clientTradingStatusBusyRef.current.add(id);
      setClientTradingStatusBusyIds((prev) => new Set(prev).add(id));
      try {
        const extra = { ...(raw.broker_details_extra_fields || {}) };
        const payload = {
          full_name: (raw.full_name || "").trim(),
          email: (raw.email || "").trim(),
          broker_name: raw.broker_name,
          is_active: nextActive === true,
          pan_number: (raw.pan_number || "").trim(),
          dob: raw.dob,
          broker_details_extra_fields: extra,
        };
        await editClients(id, payload);
        setClientMembers((prev) =>
          prev.map((m) =>
            m.id === id
              ? {
                  ...m,
                  status: nextActive ? "Active" : "Inactive",
                  raw: { ...m.raw, is_active: nextActive },
                }
              : m
          )
        );
        setEditingClient((prev) =>
          prev && prev.id === id ? { ...prev, is_active: nextActive } : prev
        );
      } catch (err) {
        console.error("Update trading status failed:", err);
        setTokenMessage({
          type: "error",
          text: err?.response?.data?.detail || err?.message || "Failed to update trading status",
        });
      } finally {
        clientTradingStatusBusyRef.current.delete(id);
        setClientTradingStatusBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    []
  );

  const openEditModal = useCallback((member) => {
    setEditingClient(member.raw || null);
    setAddClientModalOpen(true);
  }, []);

  const requestDeleteClient = useCallback((member) => {
    if (member?.id == null) return;
    setClientDeleteError(null);
    setClientToDelete(member);
  }, []);

  const closeDeleteClientDialog = useCallback(() => {
    if (clientDeleteLoading) return;
    setClientToDelete(null);
    setClientDeleteError(null);
  }, [clientDeleteLoading]);

  const confirmDeleteClient = useCallback(async () => {
    const id = clientToDelete?.id;
    if (id == null) return;
    setClientDeleteLoading(true);
    setClientDeleteError(null);
    try {
      await deleteClients(id);
      setClientMembers((prev) => prev.filter((m) => m.id !== id));
      setClientToDelete(null);
    } catch (err) {
      console.error("Delete client failed:", err);
      setClientDeleteError(err?.response?.data?.detail || err?.message || "Failed to delete client");
    } finally {
      setClientDeleteLoading(false);
    }
  }, [clientToDelete]);

  const handleAddTrader = useCallback(
    async (payload) => {
      await createTrader(payload);
      await fetchTraders();
      setTraderModalOpen(false);
      setEditingTrader(null);
    },
    [fetchTraders]
  );

  const handleEditTrader = useCallback(
    async (id, payload) => {
      await updateTrader(id, payload);
      await fetchTraders();
      setEditingTrader(null);
      setTraderModalOpen(false);
    },
    [fetchTraders]
  );

  const requestDeleteTrader = useCallback((trader) => {
    if (trader?.id == null) return;
    setTraderDeleteError(null);
    setTraderToDelete(trader);
  }, []);

  const closeDeleteTraderDialog = useCallback(() => {
    if (traderDeleteLoading) return;
    setTraderToDelete(null);
    setTraderDeleteError(null);
  }, [traderDeleteLoading]);

  const confirmDeleteTrader = useCallback(async () => {
    const id = traderToDelete?.id;
    if (id == null) return;
    setTraderDeleteLoading(true);
    setTraderDeleteError(null);
    try {
      await deleteTrader(id);
      setTradersList((prev) => prev.filter((t) => t.id !== id));
      setTraderToDelete(null);
    } catch (err) {
      console.error("Delete trader failed:", err);
      setTraderDeleteError(err?.response?.data?.detail || err?.message || "Failed to delete trader");
    } finally {
      setTraderDeleteLoading(false);
    }
  }, [traderToDelete]);

  const openEditTraderModal = useCallback((trader) => {
    setEditingTrader(trader);
    setTraderModalOpen(true);
  }, []);

  const handleChangeTraderPasswordSubmit = useCallback(async (id, payload) => {
    await changeTraderPassword(id, payload);
  }, []);

  const menuItems = useMemo(() => {
    const items = [{ id: "personal-info", label: "User Information", iconName: "PersonIcon" }];
    if (!isTrader) {
      items.push({ id: "client-account", label: "Client Account Details", iconName: "PeopleIcon" });
    }
    if (isMaster) {
      items.push({ id: "traders", label: "Traders", iconName: "BulkOrderIcon" });
    }
    items.push({ id: "groups", label: "Create Basket", iconName: "BasketIcon" });
    items.push({ id: "table-columns", label: "Preferences", iconName: "TableSortIcon" });
    return items;
  }, [isMaster, isTrader]);

  const profileName = useMemo(() => 
    auth.user?.full_name || auth.user?.username || "Anonymous",
    [auth.user?.full_name, auth.user?.username]
  );

  return (
    <div className={styles["settings-page"]}>
      <Snackbar
        open={!!tokenMessage}
        autoHideDuration={isTokenLoading ? null : 3000}
        onClose={handleCloseTokenSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={isTokenLoading ? undefined : handleCloseTokenSnackbar}
          severity={tokenMessage?.type === "error" ? "error" : tokenMessage?.type === "success" ? "success" : "info"}
          variant="filled"
          icon={isTokenLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
          sx={{ alignItems: "center" }}
        >
          {typeof tokenMessage?.text === "string" ? tokenMessage.text : ""}
        </Alert>
      </Snackbar>
      <div className={styles["settings-container"]}>
        <div className={styles["settings-sidebar"]}>
          <div className={styles["profile-section"]}>
            <div className={styles["avatar-container"]}>
              <div className={styles["avatar"]}>
                {auth.user?.profile_picture ? (
                  <img src={auth.user.profile_picture} alt="Profile" />
                ) : (
                  <img src={avatarImage} alt="Avatar" />
                )}
              </div>
              <div className={styles["camera-icon"]}>
                <img src={cameraIcon} alt="Camera" />
              </div>
            </div>
            <div className={styles["profile-name"]}>
              {profileName}
            </div>
            {auth.user?.username ? (
              <div className={styles["profile-user-id"]}>
                <span className={styles["profile-user-id-label"]}>User ID</span>
                <span className={styles["profile-user-id-value"]} title={auth.user.username}>
                  {auth.user.username}
                </span>
              </div>
            ) : null}
          </div>
          <nav className={styles["settings-nav"]} aria-label="Settings sections">
            <div className={styles["settings-scroll-root"]}>
              <div className={styles["settings-scroll-inner"]}>
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    className={`${styles["nav-item"]} ${
                      activeTab === item.id ? styles["nav-item-active"] : ""
                    }`}
                    onClick={() => navigateToSettingsTab(item.id)}
                  >
                    <span className={styles["nav-icon"]}>
                      <GlobalIcon iconName={item.iconName} className={styles["nav-icon-svg"]} />
                    </span>
                    <span className={styles["nav-label"]}>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </nav>
        </div>
        <div className={styles["settings-content"]}>
          <div className={styles["content-card"]}>
            {activeTab === "personal-info" && (
              <div className={styles["personal-info-page"]}>
                <div className={styles["content-header"]}>
                  <h1 className={styles["content-title"]}>User Information</h1>
                  <p className={styles["content-subtitle"]}>
                    Your profile details as registered on the platform
                  </p>
                </div>

                <div className={styles["personal-info-callout"]} role="status">
                  <span className={styles["personal-info-callout-icon-wrap"]} aria-hidden>
                    <InfoOutlinedIcon className={styles["personal-info-callout-mui-icon"]} />
                  </span>
                  <div className={styles["personal-info-callout-body"]}>
                    <p className={styles["personal-info-callout-title"]}>How to update your profile</p>
                    <p className={styles["personal-info-callout-text"]}>
                      To change your name, email, or phone number, please contact your respective master.
                    </p>
                  </div>
                </div>

                <div className={styles["personal-info-card"]}>
                  <div className={styles["personal-info-row"]}>
                    <span className={styles["personal-info-icon-wrap"]} aria-hidden>
                      <PersonOutlineIcon className={styles["personal-info-row-mui-icon"]} />
                    </span>
                    <div className={styles["personal-info-row-body"]}>
                      <span className={styles["personal-info-row-label"]}>Full name</span>
                      <span className={styles["personal-info-row-value"]}>
                        {personalInfoFromAuth.fullName.trim() || "—"}
                      </span>
                    </div>
                  </div>
                  <div className={styles["personal-info-row"]}>
                    <span className={styles["personal-info-icon-wrap"]} aria-hidden>
                      <EmailOutlinedIcon className={styles["personal-info-row-mui-icon"]} />
                    </span>
                    <div className={styles["personal-info-row-body"]}>
                      <span className={styles["personal-info-row-label"]}>Email address</span>
                      <span className={styles["personal-info-row-value"]}>
                        {personalInfoFromAuth.email.trim() || "—"}
                      </span>
                    </div>
                  </div>
                  <div className={`${styles["personal-info-row"]} ${styles["personal-info-row-last"]}`}>
                    <span className={styles["personal-info-icon-wrap"]} aria-hidden>
                      <PhoneIphoneOutlinedIcon className={styles["personal-info-row-mui-icon"]} />
                    </span>
                    <div className={styles["personal-info-row-body"]}>
                      <span className={styles["personal-info-row-label"]}>Phone number</span>
                      <span className={styles["personal-info-row-value"]}>
                        {String(personalInfoFromAuth.phone || "").trim() || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "client-account" && (
              <>
                <div className={styles["family-section"]}>
                  <div className={styles["family-section-header"]}>
                    <div>
                      <h1 className={styles["content-title"]}>Client Account Details</h1>
                      <p className={styles["content-subtitle"]}>
                        Manage your client sub-accounts and permissions
                      </p>
                    </div>
                    <div className={styles["client-account-header-actions"]}>
                      <input
                        ref={tokenUploadInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className={styles["client-token-upload-input"]}
                        onChange={handleTokenFileChange}
                      />
                      <Button
                        type="button"
                        variant="outlined"
                        className={styles["client-token-upload-button"]}
                        onClick={handleUploadTokensClick}
                        disabled={tokenUploadLoading}
                      >
                        {tokenUploadLoading ? "Uploading..." : "Upload Token Excel"}
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        className={styles["add-family-button"]}
                        startIcon={<GlobalIcon iconName="AddIcon" className={styles["add-family-icon"]} />}
                        onClick={() => { setEditingClient(null); setAddClientModalOpen(true); }}
                      >
                        Add Client
                      </Button>
                    </div>
                  </div>
                  <div className={styles["client-account-search-row"]}>
                    <TextField
                      className={styles["client-account-search"]}
                      placeholder="Search clients (name, client code, email, trading status, broker)…"
                      value={clientSearchQuery}
                      onChange={(e) => setClientSearchQuery(e.target.value)}
                      fullWidth
                      size="small"
                      variant="outlined"
                      disabled={clientMembersLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <GlobalIcon iconName="SearchIcon" className={styles["client-account-search-icon"]} />
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ "aria-label": "Search clients" }}
                    />
                  </div>
                  <div className={styles["family-table-wrap"]}>
                    {clientMembersError && (
                      <div className={styles["family-table-error"]}>{clientMembersError}</div>
                    )}
                    <table className={styles["family-table"]}>
                      <thead>
                        <tr>
                          <th className={styles["family-th-members"]}>Clients</th>
                          <th className={styles["family-th-status"]}>Trading Status</th>
                          <th className={styles["family-th-id"]}>Client code</th>
                          <th className={styles["family-th-email"]}>Email Address</th>
                          <th className={styles["family-th-actions"]}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientMembersLoading ? (
                          <tr>
                            <td colSpan={5} className={styles["family-table-loading"]}>
                              Loading clients…
                            </td>
                          </tr>
                        ) : clientMembers.length === 0 ? (
                          <tr>
                            <td colSpan={5} className={styles["family-table-empty"]}>
                              No clients yet. Add one by clicking the &quot;Add Client&quot; button above.
                            </td>
                          </tr>
                        ) : filteredClientMembers.length === 0 ? (
                          <tr>
                            <td colSpan={5} className={styles["family-table-empty"]}>
                              No clients match your search.
                            </td>
                          </tr>
                        ) : filteredClientMembers.map((member) => (
                          <tr key={member.id} className={styles["family-row"]}>
                            <td className={styles["family-td-members"]}>
                              <span className={styles["family-member-name"]}>{member.fullName}</span>
                            </td>
                            <td className={styles["family-td-status"]}>
                              {isMaster ? (
                                <div className={styles["family-status-switch-row"]}>
                                  <Switch
                                    checked={member.raw?.is_active === true}
                                    onChange={(e) =>
                                      handleClientTradingStatusChange(member, e.target.checked)
                                    }
                                    disabled={clientTradingStatusBusyIds.has(member.id)}
                                    inputProps={{
                                      "aria-label": `Trading status for ${member.fullName || member.brokerClientCode || "client"}`,
                                    }}
                                    sx={IOS_TRADING_STATUS_SWITCH_SX}
                                  />
                                  {clientTradingStatusBusyIds.has(member.id) ? (
                                    <CircularProgress size={12} thickness={4.5} sx={{ color: "var(--accent)" }} />
                                  ) : null}
                                </div>
                              ) : (
                                <span
                                  className={`${styles["family-status-pill"]} ${styles[`family-status-${member.status.toLowerCase()}`]}`}
                                >
                                  {member.status}
                                </span>
                              )}
                            </td>
                            <td className={styles["family-td-id"]}>{member.brokerClientCode || "—"}</td>
                            <td className={styles["family-td-email"]}>{member.email}</td>
                            <td className={styles["family-td-actions"]}>
                              <button type="button" className={`${styles["family-action-btn"]} ${styles["family-action-btn-edit"]}`} aria-label="Edit" title="Edit" onClick={() => openEditModal(member)}>
                                <GlobalIcon iconName="EditPencilIcon" className={styles["family-action-icon"]} />
                                <span>Edit</span>
                              </button>
                              <button
                                type="button"
                                className={`${styles["family-action-btn"]} ${styles["family-action-btn-regenerate"]}`}
                                aria-label="Generate token"
                                title="Generate token"
                                disabled={generatingTokenId !== null}
                                onClick={() => handleGenerateToken(member.id)}
                              >
                                <span className={generatingTokenId === member.id ? styles["family-action-icon-spin"] : ""}>
                                  {generatingTokenId === member.id ? (
                                    <CircularProgress size={14} thickness={5} sx={{ color: "inherit" }} />
                                  ) : (
                                    <GlobalIcon iconName="ReloadIcon" className={styles["family-action-icon"]} />
                                  )}
                                </span>
                                <span>{generatingTokenId === member.id ? "Generating…" : "Generate token"}</span>
                              </button>
                              <button type="button" className={`${styles["family-action-btn"]} ${styles["family-action-btn-delete"]}`} aria-label="Delete" title="Delete" onClick={() => requestDeleteClient(member)}>
                                <GlobalIcon iconName="DeleteTrashIcon" className={styles["family-action-icon"]} />
                                <span>Delete</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {activeTab === "traders" && isMaster && (
              <div className={styles["family-section"]}>
                <div className={styles["family-section-header"]}>
                  <div>
                    <h1 className={styles["content-title"]}>Traders</h1>
                    <p className={styles["content-subtitle"]}>
                      Add and manage trader accounts for your organization
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="contained"
                    className={styles["add-family-button"]}
                    startIcon={<GlobalIcon iconName="AddIcon" className={styles["add-family-icon"]} />}
                    onClick={() => {
                      setEditingTrader(null);
                      setTraderModalOpen(true);
                    }}
                  >
                    Add trader
                  </Button>
                </div>
                <div className={styles["family-table-wrap"]}>
                  {tradersError && (
                    <div className={styles["family-table-error"]}>{tradersError}</div>
                  )}
                  <table className={styles["family-table"]}>
                    <thead>
                      <tr>
                        <th className={styles["family-th-members"]}>Full name</th>
                        <th className={styles["family-th-email"]}>Email</th>
                        <th className={styles["family-th-id"]}>Username</th>
                        <th className={styles["family-th-status"]}>Master</th>
                        <th className={styles["family-th-actions"]}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tradersLoading ? (
                        <tr>
                          <td colSpan={5} className={styles["family-table-loading"]}>
                            Loading traders…
                          </td>
                        </tr>
                      ) : tradersList.length === 0 ? (
                        <tr>
                          <td colSpan={5} className={styles["family-table-empty"]}>
                            No traders yet. Add one with the button above.
                          </td>
                        </tr>
                      ) : (
                        tradersList.map((t) => (
                          <tr key={t.id} className={styles["family-row"]}>
                            <td className={styles["family-td-members"]}>
                              <span className={styles["family-member-name"]}>{t.full_name || "—"}</span>
                            </td>
                            <td className={styles["family-td-email"]}>{t.email || "—"}</td>
                            <td className={styles["family-td-id"]}>{t.username || "—"}</td>
                            <td className={styles["family-td-status"]}>
                              <span className={styles["family-status-pill"]}>{t.master || "—"}</span>
                            </td>
                            <td className={styles["family-td-actions"]}>
                              <button
                                type="button"
                                className={`${styles["family-action-btn"]} ${styles["family-action-btn-edit"]}`}
                                aria-label="Edit"
                                title="Edit"
                                onClick={() => openEditTraderModal(t)}
                              >
                                <GlobalIcon iconName="EditPencilIcon" className={styles["family-action-icon"]} />
                                <span>Edit</span>
                              </button>
                              <button
                                type="button"
                                className={`${styles["family-action-btn"]} ${styles["family-action-btn-regenerate"]}`}
                                aria-label="Change password"
                                title="Change password"
                                onClick={() => setTraderPasswordTarget(t)}
                              >
                                <GlobalIcon iconName="LockIcon" className={styles["family-action-icon"]} />
                                <span>Password</span>
                              </button>
                              <button
                                type="button"
                                className={`${styles["family-action-btn"]} ${styles["family-action-btn-delete"]}`}
                                aria-label="Delete"
                                title="Delete"
                                onClick={() => requestDeleteTrader(t)}
                              >
                                <GlobalIcon iconName="DeleteTrashIcon" className={styles["family-action-icon"]} />
                                <span>Delete</span>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "table-columns" && <TableColumnPreferencesSection />}

            {activeTab === "groups" && (
              <div className={styles["basket-page-layout"]}>
                <div className={styles["basket-form-panel"]}>
                  <div className={styles["content-header"]}>
                    <h1 className={styles["content-title"]}>Create Basket</h1>
                    <p className={styles["content-subtitle"]}>
                      Create a basket to trade and manage multiple accounts together.
                    </p>
                  </div>
                  <form onSubmit={handleBasketFormSubmit} className={styles["basket-form-fields"]}>
                    {basketFormError ? (
                      <Alert severity="error" onClose={() => setBasketFormError(null)} sx={{ mb: 0 }}>
                        {basketFormError}
                      </Alert>
                    ) : null}
                    <div className={styles["form-group"]}>
                      <label className={styles["form-label"]} htmlFor="settings-basket-name">
                        Basket Name
                      </label>
                      <TextField
                        id="settings-basket-name"
                        name="basketName"
                        value={basketName}
                        onChange={(e) => setBasketName(e.target.value)}
                        placeholder="Directional"
                        fullWidth
                        size="small"
                        className={styles["basket-form-input"]}
                        variant="outlined"
                      />
                    </div>
                    <div className={styles["form-group"]}>
                      <label className={styles["form-label"]} htmlFor="settings-basket-description">
                        Descriptions
                      </label>
                      <TextField
                        id="settings-basket-description"
                        name="basketDescription"
                        value={basketDescription}
                        onChange={(e) => setBasketDescription(e.target.value)}
                        placeholder="Type here"
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={8}
                        size="small"
                        className={`${styles["basket-form-input"]} ${styles["basket-form-input-textarea"]}`}
                        variant="outlined"
                      />
                    </div>
                    <div className={styles["basket-form-actions"]}>
                      <Button type="button" variant="outlined" onClick={handleBasketFormCancel} className={styles["basket-cancel-btn"]} disabled={basketSubmitting}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" className={styles["basket-submit-btn"]} disabled={basketSubmitting || !basketName.trim()}>
                        {basketSubmitting ? "Creating…" : "Create Basket"}
                      </Button>
                    </div>
                  </form>
                </div>
                <div className={styles["basket-list-panel"]}>
                  <h2 className={styles["basket-all-title"]}>
                    All Baskets
                    <span className={styles["basket-all-title-count"]}> ({baskets.length})</span>
                  </h2>
                  {basketsLoading && baskets.length === 0 ? (
                    <div className={styles["client-health-loading"]}>
                      <CircularProgress size={28} sx={{ mb: 1 }} />
                      <div>Loading baskets…</div>
                    </div>
                  ) : basketsListError && baskets.length === 0 ? (
                    <div className={styles["client-health-empty"]}>{basketsListError}</div>
                  ) : baskets.length === 0 ? (
                    <div className={styles["client-health-empty"]}>No baskets yet. Create one on the left.</div>
                  ) : (
                    <div className={styles["basket-list-body"]}>
                      {clientsForBasketError ? (
                        <Alert severity="warning" sx={{ mb: 2, fontFamily: "Inter, sans-serif" }}>
                          {clientsForBasketError} — client add/remove may be limited until this loads.
                        </Alert>
                      ) : null}
                      <div
                        className={styles["basket-pills-scroll"]}
                        role="region"
                        aria-label="Basket list, scroll if needed"
                      >
                        <div className={styles["basket-pills-grid"]} role="group" aria-label="All baskets">
                          {baskets.map((b) => {
                            const isSelected = selectedBasketId === b.id;
                            return (
                              <button
                                key={b.id}
                                type="button"
                                className={`${styles["basket-pill"]} ${isSelected ? styles["basket-pillSelected"] : ""}`}
                                title={b.description || undefined}
                                aria-pressed={isSelected}
                                onClick={() =>
                                  setSelectedBasketId((prev) => (prev === b.id ? null : b.id))
                                }
                              >
                                {b.basket_name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {!selectedBasket ? (
                        <p className={styles["basket-select-hint"]}>
                          Select a basket to add or remove clients, or delete the basket.
                        </p>
                      ) : (
                        <BasketManageSection
                          basket={selectedBasket}
                          members={selectedBasketMembers}
                          allClients={marginClients}
                          clientsLoading={marginClientsLoading}
                          isAdding={basketClientOpLoading.adding}
                          removingClientId={basketClientOpLoading.removingId}
                          isDeletingBasket={basketClientOpLoading.deleting}
                          onAddClients={handleAddClientsToBasket}
                          onRemoveMember={handleRemoveBasketMember}
                          onDeleteBasket={handleDeleteSelectedBasket}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <RemoveEntityDialog
        open={Boolean(clientToDelete)}
        onClose={closeDeleteClientDialog}
        title="Remove this client?"
        confirmLabel="Remove client"
        loading={clientDeleteLoading}
        error={clientDeleteError}
        onConfirm={confirmDeleteClient}
      >
        <>
          This will permanently remove{" "}
          <strong>
            {clientToDelete?.fullName?.trim() ||
              clientToDelete?.brokerClientCode ||
              "this client"}
          </strong>{" "}
          from your account. Broker links and history for this client may be affected. This cannot be undone.
        </>
      </RemoveEntityDialog>

      <RemoveEntityDialog
        open={Boolean(traderToDelete)}
        onClose={closeDeleteTraderDialog}
        title="Remove this trader?"
        confirmLabel="Remove trader"
        loading={traderDeleteLoading}
        error={traderDeleteError}
        onConfirm={confirmDeleteTrader}
      >
        <>
          This will permanently remove{" "}
          <strong>{traderToDelete?.full_name?.trim() || traderToDelete?.username || "this trader"}</strong>
          {traderToDelete?.email ? (
            <>
              {" "}
              (<strong>{traderToDelete.email}</strong>)
            </>
          ) : null}{" "}
          from your organization. This cannot be undone.
        </>
      </RemoveEntityDialog>

      <AddFamilyMemberModal
        isOpen={addClientModalOpen}
        onClose={() => { setAddClientModalOpen(false); setEditingClient(null); }}
        onAdd={handleAddClientMember}
        editClient={editingClient}
        onEdit={handleEditClientMember}
      />
      {isMaster ? (
        <>
          <AddTraderModal
            isOpen={traderModalOpen}
            onClose={() => {
              setTraderModalOpen(false);
              setEditingTrader(null);
            }}
            onAdd={handleAddTrader}
            editTrader={editingTrader}
            onEdit={handleEditTrader}
          />
          <ChangeTraderPasswordModal
            isOpen={Boolean(traderPasswordTarget)}
            trader={traderPasswordTarget}
            onClose={() => setTraderPasswordTarget(null)}
            onSubmit={handleChangeTraderPasswordSubmit}
          />
        </>
      ) : null}
    </div>
  );
}

export default SettingsPage;
